import { useRef } from 'react';
import { DotLottieReact, setWasmUrl, type DotLottie } from '@lottiefiles/dotlottie-react';
import wasmUrl from '@lottiefiles/dotlottie-web/dotlottie-player.wasm?url';

// Served from our own bundle rather than the player's default CDN.
setWasmUrl(wasmUrl);

/** The sequence dissolves to transparent from here; the cards show through. */
const HANDOVER_FRAME = 40;

/** One file per card count; the pack opens on as many cards as the slot holds. */
export const revealAnimationUrl = (cards: number) => {
  const n = Math.min(Math.max(cards, 1), 3);
  return `/video/animations/CD-${n}Card${n === 1 ? '' : 's'}-Lottie.lottie`;
};

export type RevealAnimationOverlayProps = {
  cards: number;
  /** Fired once the animation starts dissolving, so the reveal screen can mount beneath it. */
  onHandover: () => void;
  /** Fired once at the end, or on any load/render failure. */
  onComplete: () => void;
};

export default function RevealAnimationOverlay({
  cards,
  onHandover,
  onComplete,
}: RevealAnimationOverlayProps) {
  // Refs so the player's listeners always call the latest callbacks; the parent re-renders every second.
  const handoverRef = useRef(onHandover);
  handoverRef.current = onHandover;
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;
  const handedOver = useRef(false);
  const done = useRef(false);
  const attached = useRef<DotLottie | null>(null);

  const handover = () => {
    if (handedOver.current) return;
    handedOver.current = true;
    handoverRef.current();
  };
  const finish = () => {
    if (done.current) return;
    done.current = true;
    handover();
    completeRef.current();
  };

  const attach = (player: DotLottie | null) => {
    if (!player || attached.current === player) return;
    attached.current = player;
    player.addEventListener('frame', ({ currentFrame }) => {
      if (currentFrame >= HANDOVER_FRAME) handover();
    });
    player.addEventListener('complete', finish);
    player.addEventListener('loadError', finish);
    player.addEventListener('renderError', finish);
  };

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none">
      <DotLottieReact
        src={revealAnimationUrl(cards)}
        autoplay
        loop={false}
        layout={{ fit: 'cover', align: [0.5, 0.5] }}
        dotLottieRefCallback={attach}
        className="h-full w-full"
      />
    </div>
  );
}
